import {useSelector} from 'react-redux'
import {getAnonymousId, getLocaleCode} from 'modules/meta/selectors'
import {
  getAnchorHotelId,
  getSearchId,
  getSearchParameters
} from 'modules/sapiSearch/selectors'
import {metaLandingParamKeys} from 'modules/search/constants'
import {
  getLandingItinerary,
  getSearchHasBookingHint,
  isLandingItinerary
} from 'modules/search/selectors'
import {getUrlParams as getSearchUrlParams} from 'modules/search/selectors'
import {omit} from 'ramda'
import {generateAccommodationPageURL} from 'utils/url'

import {useIsEmbeddedWithBrand} from '@vio/core/src/_web/brand/hooks/useIsEmbeddedWithBrand'
import {TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY} from '@vio/core/src/auth/modules/actions'
import {mapSearchToRoomsFilters} from '@vio/core/src/room/business/utils/filters'
import {validateSearchParamDates} from '@vio/core/src/searchParams/validateSearchParamDates'

import {DF_TOAST_PARAM, SHOW_ALL_OFFERS_PARAM} from '../constants'
import {useFiltersForm} from './useFiltersForm'
import {useGetSRPSearchFlow} from './useGetSRPSearchFlow'
import {useIsSeoSRP} from './useIsSeoSRP'

const filterKeys = [
  'freeCancellation',
  'amenities',
  'dealFreeze' // Added as a part of c747927f-deal-freeze-srp
]

const TEMPORARY_VIO_CLICKER_PARAMS = [
  'clickedOffer',
  'clickedBase',
  'clickedTaxes',
  'clickedFees',
  'clickedProvider',
  'clickedUserIp',
  'redirectId'
]

interface AccommodationPageUrlOptions {
  // Added as a part of c747927f-deal-freeze-srp
  showDealFreezeToast?: boolean
  showAllOffers?: boolean
}

type ShowAccommodationPage = (params: {
  hotelId: string
  // Added as a part of c747927f-deal-freeze-srp
  showDealFreezeToast?: boolean
}) => void

export const useShowAccommodationPage = () => {
  const isSeoSRP = useIsSeoSRP()
  const locale = useSelector(getLocaleCode)
  const isEmbedded = useIsEmbeddedWithBrand()
  const {getAppliedFilters} = useFiltersForm(filterKeys)
  const appliedFilters = getAppliedFilters()
  const activeRoomsFilters = mapSearchToRoomsFilters(appliedFilters)

  const anchorHotelId = useSelector(getAnchorHotelId)
  const anonymousId = useSelector(getAnonymousId)
  const {checkIn: searchParametersCheckIn, checkOut: searchParametersCheckOut} =
    useSelector(getSearchParameters) || {}
  const srpSearchId = useSelector(getSearchId)

  const urlParams = useSelector(getSearchUrlParams)

  const hasBookingHint = useSelector(getSearchHasBookingHint)
  const landingItinerary = useSelector(getLandingItinerary)

  const srpSearchFlow = useGetSRPSearchFlow()

  const getAccommodationPageParams = (
    hotelId: string,
    {
      showDealFreezeToast = false,
      showAllOffers = false
    }: AccommodationPageUrlOptions
  ) => {
    const validatedUrlParams = validateSearchParamDates(
      omit(TEMPORARY_VIO_CLICKER_PARAMS, urlParams) as Record<string, string>
    )
    const {checkIn, checkOut, ...urlParamsWithoutDates} = validatedUrlParams

    const resolvedCheckIn = searchParametersCheckIn || checkIn
    const resolvedCheckOut = searchParametersCheckOut || checkOut

    const isAnchor = hotelId === anchorHotelId
    const canCarryLandingToken = isLandingItinerary(
      {hotelId, checkIn: resolvedCheckIn, checkOut: resolvedCheckOut},
      landingItinerary
    )

    return {
      ...(canCarryLandingToken
        ? urlParamsWithoutDates
        : omit(metaLandingParamKeys, urlParamsWithoutDates)),
      srpSearchId,
      srpSearchFlow,
      isAnchor: isAnchor ? '1' : '0',
      anonymousId,
      checkIn: resolvedCheckIn,
      checkOut: resolvedCheckOut,
      ...(activeRoomsFilters.length
        ? {roomFilters: activeRoomsFilters.join(',')}
        : {}),
      // Added as a part of c747927f-deal-freeze-srp
      ...(showDealFreezeToast ? {[DF_TOAST_PARAM]: '1'} : {}),
      ...(showAllOffers ? {[SHOW_ALL_OFFERS_PARAM]: '1'} : {})
    }
  }

  const getAccommodationPageUrl = (
    hotelId: string,
    options: AccommodationPageUrlOptions = {}
  ) => {
    // Canonical URL for SEO SRP
    if (isSeoSRP) return `/Hotel/${hotelId}/${locale}`

    const isAnchor = hotelId === anchorHotelId
    return generateAccommodationPageURL(
      hotelId,
      locale,
      getAccommodationPageParams(hotelId, options),
      isAnchor,
      hasBookingHint
    )
  }

  const showAccommodationPage: ShowAccommodationPage = ({
    hotelId,
    // Added as a part of c747927f-deal-freeze-srp
    showDealFreezeToast = false
  }) => {
    localStorage.setItem('navigationToAccommodationPage', Date.now().toString())

    const accommodationPageURL = getAccommodationPageUrl(hotelId, {
      showDealFreezeToast
    })
    setTimeout(() => {
      window.open(accommodationPageURL, isEmbedded ? '_self' : '_blank')
    }, TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY)
  }

  return {
    getAccommodationPageUrl,
    showAccommodationPage
  }
}
